import {
  createSelector,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import type { BriefProject, BriefProjectId } from 'types/brief'

// Extended BriefProject type for state management
type BriefProjectWithState = BriefProject & {
  deleting?: boolean
  updating?: boolean
}
import { createAppSlice } from '@disco/utils/slices'
import {
  createBriefProject as createBriefProjectApi,
  listBriefProjects as listBriefProjectsApi,
  getBriefProject as getBriefProjectApi,
  deleteBriefProject as deleteBriefProjectApi,
  updateBriefProject as updateBriefProjectApi,
} from '@disco/api/briefs'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import captureError from '@disco/utils/captureError'

export interface BriefProjectsState {
  loading: boolean
  loaded: boolean
  error: SerializedError | null
  briefProjects: Record<BriefProjectId, BriefProjectWithState>
}

const initialState: BriefProjectsState = {
  loading: false,
  loaded: false,
  error: null,
  briefProjects: {},
}

const defaultRejectedHandler = (
  state: BriefProjectsState,
  action: PayloadAction<unknown, string, any, SerializedError>,
) => {
  state.loading = false
  state.error = action.error
}

const defaultPendingHandler = (state: BriefProjectsState) => {
  state.loading = true
  state.loaded = false
}

const defaultFulfilledHandler = (
  state: BriefProjectsState,
  action: PayloadAction<BriefProject>,
) => {
  state.loading = false
  state.loaded = true
  state.error = null
  state.briefProjects[action.payload.id] = action.payload
}

const defaultHandlers = {
  pending: defaultPendingHandler,
  fulfilled: defaultFulfilledHandler,
  rejected: defaultRejectedHandler,
}

const errorOnlyHandlers = {
  rejected: (state: BriefProjectsState, { payload }) => {
    state.error = new Error(payload.error)
  },
}

const briefProjectsSlice = createAppSlice({
  name: 'briefProjects',
  initialState,
  reducers: create => ({
    setBriefProjects: create.reducer(
      (state, action: PayloadAction<BriefProject[]>) => {
        state.loading = false
        state.loaded = true
        state.error = null
        // Add/update brief projects without resetting existing ones
        action.payload.forEach(project => {
          state.briefProjects[project.id] = project
        })
      },
    ),
    loadBriefProjects: create.asyncThunk(
      async () => {
        const briefProjects = await listBriefProjectsApi()
        return briefProjects
      },
      {
        pending: defaultPendingHandler,
        fulfilled: (state, action: PayloadAction<BriefProject[]>) => {
          state.loading = false
          state.loaded = true
          state.error = null
          // Add/update brief projects without resetting existing ones
          action.payload.forEach(project => {
            state.briefProjects[project.id] = project
          })
        },
        rejected: defaultRejectedHandler,
      },
    ),
    loadBriefProject: create.asyncThunk(
      async (briefProjectId: BriefProjectId) => {
        const briefProject = await getBriefProjectApi(briefProjectId)
        return briefProject
      },
      defaultHandlers,
    ),
    createBriefProject: create.asyncThunk<
      BriefProject,
      string,
      { rejectValue: { error: string } }
    >(async (name, thunkApi) => {
      try {
        const newProject = await createBriefProjectApi(name)
        pushUINotification({
          title: 'Brief project created!',
          message: `Brief project "${name}" has been created successfully.`,
          level: 'success',
        })
        return newProject
      } catch (error) {
        captureError(error)
        pushUINotification({
          message: parseServerError(error.body),
          level: 'error',
        })
        return thunkApi.rejectWithValue({ error: parseServerError(error.body) })
      }
    }, defaultHandlers),
    deleteBriefProject: create.asyncThunk<
      BriefProjectId,
      BriefProjectId,
      { rejectValue: { error: string } }
    >(
      async (briefProjectId, thunkApi) => {
        try {
          await deleteBriefProjectApi(briefProjectId)
          pushUINotification({
            title: 'Brief project deleted!',
            message: 'The brief project and all its briefs have been deleted.',
            level: 'success',
          })
          return briefProjectId
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const project = state.briefProjects[meta.arg]
          if (project) {
            state.briefProjects[meta.arg] = { ...project, deleting: true }
          }
        },
        fulfilled: (state, action: PayloadAction<BriefProjectId>) => {
          delete state.briefProjects[action.payload]
        },
        rejected: (state, { meta, payload }) => {
          const project = state.briefProjects[meta.arg]
          if (project) {
            state.briefProjects[meta.arg] = { ...project, deleting: false }
          }
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    updateBriefProject: create.asyncThunk<
      BriefProject,
      { briefProjectId: BriefProjectId; updates: Partial<BriefProject> },
      { rejectValue: { error: string } }
    >(
      async ({ briefProjectId, updates }, thunkApi) => {
        try {
          const updatedProject = await updateBriefProjectApi(
            briefProjectId,
            updates,
          )
          pushUINotification({
            title: 'Brief project updated!',
            message: `Brief project has been updated successfully.`,
            level: 'success',
          })
          return updatedProject
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const project = state.briefProjects[meta.arg.briefProjectId]
          if (project) {
            state.briefProjects[meta.arg.briefProjectId] = {
              ...project,
              updating: true,
            }
          }
        },
        fulfilled: (state, action: PayloadAction<BriefProject>) => {
          const project = state.briefProjects[action.payload.id]
          if (project) {
            state.briefProjects[action.payload.id] = {
              ...action.payload,
              updating: false,
            }
          }
        },
        rejected: (state, { meta, payload }) => {
          const project = state.briefProjects[meta.arg.briefProjectId]
          if (project) {
            state.briefProjects[meta.arg.briefProjectId] = {
              ...project,
              updating: false,
            }
          }
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
  }),
})

export const {
  loadBriefProjects,
  loadBriefProject,
  createBriefProject,
  deleteBriefProject,
  updateBriefProject,
  setBriefProjects,
} = briefProjectsSlice.actions

// Selectors
export const getBriefProject = (
  state,
  { briefProjectId }: { briefProjectId: BriefProjectId },
): BriefProjectWithState | undefined => {
  return state.entities.briefProjects.briefProjects[briefProjectId]
}

export const getAllBriefProjects = createSelector(
  [state => state.entities.briefProjects.briefProjects],
  briefProjects => Object.values(briefProjects),
)

export const getBriefProjectsLoading = (state): boolean => {
  return state.entities.briefProjects.loading
}

export const getBriefProjectsLoaded = (state): boolean => {
  return state.entities.briefProjects.loaded
}

export const getBriefProjectsError = (state): SerializedError | null => {
  return state.entities.briefProjects.error
}

export const briefProjectsReducer = briefProjectsSlice.reducer
